import Swiper from 'swiper';
import { Navigation, Pagination, A11y, Keyboard } from 'swiper/modules';

/**
 * Read at mount time rather than cached at module load, so a viewer who changes
 * the OS setting mid-session gets the new behaviour on the next slider built.
 */
const prefersReducedMotion = () =>
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Case Studies Slider.
 *
 * One Swiper per industry panel, plus the selector that swaps between them.
 *
 * Sliders are built LAZILY — the first time their panel is actually on screen
 * — and never up front. A Swiper constructed inside a display:none panel
 * measures every width as zero and writes `width: 0px` onto its slides, which
 * it then keeps until something tells it to re-measure. Building it only once
 * the panel is visible sidesteps that entirely rather than trying to correct
 * it afterwards.
 */
window.CaseStudiesSlider = (initial = '', labels = {}) => {
	return {
		active: initial,
		menu: false,

		/** slug → industry name, rendered server-side. See the `label` getter. */
		labels,

		/** slug → Swiper instance. Only groups with 2+ studies ever get one. */
		sliders: {},

		init() {
			// Mount off the element's OWN size rather than off a scheduler tick.
			// $nextTick looked like the natural hook, but it doesn't reliably
			// run before the panel has been laid out, so the slider was built
			// against a zero-width box — or, when the tick never fired, not
			// built at all: the pagination stayed empty and collapsed to 0px
			// high. A ResizeObserver can't miss it. The panel going from
			// display:none to visible IS a resize, so the observer fires exactly
			// when there is something real to measure, and fires again on any
			// later container resize, where mountSlider re-measures instead.
			const observer = new ResizeObserver((entries) => {
				entries.forEach(({ target }) => {
					if (target.clientWidth > 0) this.mountSlider(target.dataset.csSlider);
				});
			});

			this.$el
				.querySelectorAll('[data-cs-slider][data-cs-sliderable="true"]')
				.forEach((el) => observer.observe(el));
		},

		select(slug) {
			// Just state. The observer above handles the slider.
			this.active = slug;

			// Via closeMenu(), not `this.menu = false`. Choosing from the
			// dropdown hides the <ul> the chosen button lives in, and a
			// focused element inside a display:none subtree drops focus to
			// <body> — the keyboard user loses their place entirely. closeMenu
			// puts focus back on the trigger, which is where they were. It
			// no-ops when the menu isn't open, so the desktop list (where
			// focus should simply stay on the clicked button) is unaffected.
			this.closeMenu();
		},

		/**
		 * Only the slide on screen may be interactive.
		 *
		 * Swiper keeps every slide in the DOM and just translates the rest out
		 * of view, and its a11y module does not mark them hidden — so the
		 * off-screen "See Full Case Study" links sat in the tab order, sending
		 * focus to content positioned outside the card. `inert` removes them
		 * from the tab order and the accessibility tree together; unlike
		 * aria-hidden it cannot leave a focusable element behind inside a
		 * hidden subtree.
		 *
		 * slidesPerView is 1 and loop is off, so "on screen" is exactly the
		 * slide at activeIndex.
		 */
		syncSlideInteractivity(swiper) {
			swiper.slides.forEach((slide, i) => {
				slide.inert = i !== swiper.activeIndex;
			});
		},

		/**
		 * Build this industry's slider, or re-measure it if it already exists.
		 */
		mountSlider(slug) {
			const el = this.$el.querySelector(`[data-cs-slider="${slug}"]`);

			// Groups with a single study render the same markup but never get a
			// slider — there is nowhere to slide to.
			if (!el || el.dataset.csSliderable !== 'true') return;

			if (this.sliders[slug]) {
				this.sliders[slug].update();
				return;
			}

			const panel = el.parentElement;

			this.sliders[slug] = new Swiper(el, {
				modules: [Navigation, Pagination, A11y, Keyboard],
				// 350ms: the lpd-transition curve spends its tail overshooting and
				// settling, so a duration that reads fine on a fade feels sluggish
				// on a slide. Zero when the viewer has asked for reduced motion —
				// the slide still changes, it just arrives instead of travelling.
				speed: prefersReducedMotion() ? 0 : 350,
				slidesPerView: 1,
				spaceBetween: 32,

				// No loop, deliberately: the design greys the arrows out at the
				// ends, which only means something if there are ends.
				loop: false,
				watchOverflow: true,

				// Belt and braces for the measuring problem above — Swiper
				// re-measures itself if the container's size or visibility
				// changes for any reason this component didn't drive.
				observer: true,
				observeParents: true,

				keyboard: { enabled: true, onlyInViewport: true },

				a11y: {
					enabled: true,
					prevSlideMessage: 'Previous case study',
					nextSlideMessage: 'Next case study',
					paginationBulletMessage: 'Go to case study {{index}}',
				},

				navigation: {
					prevEl: panel.querySelector('[data-cs-prev]'),
					nextEl: panel.querySelector('[data-cs-next]'),
				},

				pagination: {
					el: panel.querySelector('[data-cs-pagination]'),
					clickable: true,
					renderBullet: (index, className) =>
						`<button type="button" class="${className}" aria-label="Go to case study ${index + 1}"><span></span></button>`,
				},

				// slideChange fires as soon as activeIndex moves, so the
				// incoming slide is reachable immediately and the outgoing one
				// leaves the tab order while it is still travelling.
				// TransitionEnd repeats it as a backstop for any move Swiper
				// completes without a slideChange (a snap back to the same
				// slide after a short drag).
				on: {
					slideChange: (sw) => this.syncSlideInteractivity(sw),
					slideChangeTransitionEnd: (sw) =>
						this.syncSlideInteractivity(sw),
				},
			});

			// The starting state is set here rather than from an `init`
			// handler in the object above. Measured: the two slideChange
			// handlers fire reliably, but init did not mark the slides at all
			// — the second slide stayed in the tab order until something moved
			// the slider. Calling it outright removes the question of when
			// Swiper considers itself initialised.
			this.syncSlideInteractivity(this.sliders[slug]);
		},

		closeMenu() {
			if (!this.menu) return;
			this.menu = false;
			this.$el.querySelector('.cs-industry-trigger')?.focus();
		},

		/**
		 * Label for the mobile trigger.
		 *
		 * Looked up from `labels` rather than read off the active button in the
		 * DOM. A getter that only queries the DOM registers no reactive
		 * dependency, so Alpine ran it once — before the aria-current bindings
		 * existed — got nothing, and never re-ran it, leaving the trigger blank.
		 * Keying off `this.active` makes it track the state that actually drives
		 * the panel.
		 */
		get label() {
			return this.labels[this.active] ?? '';
		},

		get status() {
			return this.label ? `Showing ${this.label} case studies.` : '';
		},
	};
};
